import { useMemo } from "react";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { dayjs, formatDateTimeWithZone } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { IReferral } from "@/referrals/referral";

interface ReferralActivity {
  message: React.ReactNode;
  event_date: string;
}

const ReferralActivity = ({ message, event_date }: ReferralActivity) => (
  <Stack align="start" gap={2}>
    {message}
    <Text variant="label" className="text-subtle">
      {formatDateTimeWithZone(event_date)}
    </Text>
  </Stack>
);

interface ReferralActivityListProps {
  referral: IReferral;
}

export const ReferralActivityList = ({
  referral,
}: ReferralActivityListProps) => {
  const activities = useMemo(
    () =>
      [...referral.payouts]
        .sort((a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)))
        .map((payout) => ({
          id: payout.id,
          message: (
            <Text>
              Referral payment made to{" "}
              <span className="text-sm font-semibold text-brand">
                {getCustomerNameOrPhone(referral.advocate)}
              </span>{" "}
              of {formatMoneyCents(payout.amount)}
            </Text>
          ),
          event_date: payout.created_at,
        })),
    [referral.advocate, referral.payouts],
  );

  if (!activities.length) {
    return (
      <Empty className={emptyStateVariants({ layout: "inline" })}>
        <EmptyHeader>
          <EmptyDescription>
            No payment history for this referral
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Stack align="start" gap={6} className="mt-6">
      {activities.map((activity) => (
        <ReferralActivity key={activity.id} {...activity} />
      ))}
    </Stack>
  );
};
